import { Box } from '@mui/material';
import { EventSession } from '@types';
import React, { useState } from 'react';
import { Typography, IconButton, Icon } from '@elements';
import EventTicket from '@components/event-ticket';
import { Link } from 'react-router-dom';
import { useEventSessionsPage } from '@hooks/useEventSessions';
import { SEASON_PASS_EVENT_CONSIST_TYPES } from '@api/event-session';
import PrevIcon from '@images/prev.svg';
import NextIcon from '@images/next.svg';
import Loader from '@components/loader/Loader';
import num_word from '@utils/num_word';

const PAGE_SIZE = 20;

const normalizeEventSession = (event: EventSession): EventSession => ({
  ...event,
  seats: event.seats ?? [],
  eventSessionTypeName: event.eventSessionTypeName ?? event.eventTypeName,
});

const SeasonPassTickets = () => {
  const [pageNumber, setPageNumber] = useState(0);
  const { data, isFetching } = useEventSessionsPage(
    SEASON_PASS_EVENT_CONSIST_TYPES,
    pageNumber,
    PAGE_SIZE
  );
  const content = data?.content ?? [];

  const hasNextPage = (data?.totalPages ?? 0) > 0 && pageNumber < (data?.totalPages ?? 1) - 1;
  const hasPrevPage = pageNumber > 0;

  const handleNextPage = () => {
    if (hasNextPage) setPageNumber((p) => p + 1);
  };

  const handlePrevPage = () => {
    if (hasPrevPage) setPageNumber((p) => p - 1);
  };

  if (isFetching && content.length === 0) {
    return <Loader isShowLoader />;
  }

  const seatsCount = content.reduce((acc, es) => acc + (es.seats ?? []).length, 0);

  if (!data || content.length === 0) {
    return (
      <Box>
        <Typography color="white" fontSize={22} sx={{ mb: '26px' }}>
          У вас 0 {num_word(0, ['абонемент', 'абонемента', 'абонементов'])} и 0{' '}
          {num_word(0, ['место', 'места', 'мест'])}
        </Typography>
        <Typography>Ничего не найдено</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: '414px' }}>
      <Typography color="white" fontSize={22} sx={{ mb: '26px' }}>
        У вас {content.length}{' '}
        {num_word(content.length, ['абонемент', 'абонемента', 'абонементов'])} и {seatsCount}{' '}
        {num_word(seatsCount, ['место', 'места', 'мест'])}
      </Typography>
      <Box sx={{ display: 'flex', flexDirection: 'column', rowGap: '16px' }}>
        {content.map((eventSession) => (
          <Link
            style={{ textDecoration: 'none' }}
            to={`events/${eventSession.id}`}
            key={eventSession.id}
          >
            <EventTicket event={normalizeEventSession(eventSession)} />
          </Link>
        ))}
      </Box>
      {data.totalPages > 1 && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: '16px',
            mt: '24px',
            mb: '24px',
          }}
        >
          <IconButton
            onClick={handlePrevPage}
            disabled={!hasPrevPage}
            sx={{
              opacity: hasPrevPage ? 1 : 0.5,
              cursor: hasPrevPage ? 'pointer' : 'not-allowed',
            }}
          >
            <Icon src={PrevIcon} width={24} height={24} />
          </IconButton>
          <Typography>
            {pageNumber + 1} / {data.totalPages}
          </Typography>
          <IconButton
            onClick={handleNextPage}
            disabled={!hasNextPage}
            sx={{
              opacity: hasNextPage ? 1 : 0.5,
              cursor: hasNextPage ? 'pointer' : 'not-allowed',
            }}
          >
            <Icon src={NextIcon} width={24} height={24} />
          </IconButton>
        </Box>
      )}
    </Box>
  );
};

export default SeasonPassTickets;
